import { z } from 'zod'
import { AdviesSetting } from './advies.js'
import { BelangenverklaringSetting } from './belangenverklaring.js'
import { Block, CompartimentId, CriterionId, DefinitieId, SectionId, FieldId } from './id.js'
import { Rule } from './rule.js'
import { WerkverzamelingSetting } from './answer.js'
import { TextDraft } from './text.js'
import { TemplateOrigin } from './templateOrigin.js'
import { COMPONENTS, type Control, FieldOrigin } from './field.js'

/**
 * De definitie zoals hij eruitziet zolang de inrichter er nog aan werkt.
 *
 * **Waarom dit bestaat.** `DefinitieConcept.stand` was een strikte `Definitie`, en
 * daarin past geen werk in uitvoering: `secties` eist minstens één sectie, elke
 * sectie minstens één veld, elke `Tekst` beide talen niet-leeg, en een keuzeveld
 * minstens één optie. Een nieuw leeg formulier was dus niet op te slaan, een sectie
 * zonder velden niet, en een veld waarvan alleen het Nederlandse label er staat ook
 * niet. Ontwerp 8.1 punt 2 eist juist het omgekeerde: elke conceptstand staat in
 * `form_definition`, met historie per persoon en moment (D-45). Zonder deze boom
 * heeft de configurator geen opslag, is D-13 (zichtbaar welke vertaling ontbreekt)
 * niet uit te drukken, en vindt R-145 per constructie nooit iets.
 *
 * **De strikte `Definitie` verandert niet, en dat is de kern.** Een `Publicatie`
 * draagt een `Definitie` (R-99), elke pod parseert die opnieuw, en dat is de laatste
 * plek waar nog blijkt of een gepubliceerd formulier compleet is. Zou de strengheid
 * uit het schema naar de poort verhuizen, dan hangt een veld zonder label af van een
 * poort zonder fout in plaats van van een schema dat het niet toelaat. Twee bomen
 * dus, één richting: `verhard` is de enige brug.
 *
 * **Waarom deze boom is uitgeschreven en niet uit `Veld` afgeleid.** Er variëren twee
 * dingen tegelijk: de leaf (`Tekst` tegen `TextDraft`) en de optionaliteit (een
 * criterium zonder schaal, een verwijzing zonder bron). Eén parameter door de boom
 * threaden lost het eerste op; het tweede kan er niet bij, want een helper die een
 * eigenschap optioneel maakt levert in beide instantiaties `X | undefined` en dan is
 * het strikte type weg. Een lus over `Veld.options` valt ook af, en dat staat al in
 * `veld.ts` bij `withoutSettings`: een lus levert één tak met een unie in de
 * discriminant en dan discrimineert Zod niet meer. Wat overblijft is uitschrijven en
 * de overeenkomst met een test vastzetten, zoals `schema.test.ts` de kolommen aan de
 * vormen bindt: `concept.test.ts` vergelijkt per controltype de sleutelverzameling
 * van deze boom met die van `Veld`, dus een eigenschap die aan één kant bijkomt laat
 * de suite vallen.
 *
 * **Wat hier niet losser is: de regels.** `regels` blijft `Regel`, dus een regel wordt
 * pas opgeslagen als hij compleet is. Dat kan, want een regel komt in een dialoog tot
 * stand en niet op het canvas; een veld verschijnt bij het neerzetten en moet meteen
 * bewaard kunnen worden, een halve regel niet. Blijkt dat bij het bouwen van de
 * regeltab (D-14 tot D-21) toch niet te houden, dan is `ConceptRegel` werk van die
 * opdracht en niet van deze.
 */

/** Een keuzeoptie in wording: waarde en label mogen nog leeg zijn. */
const OptionDraft = z.strictObject({ waarde: z.string(), label: TextDraft })

/**
 * Zonder `min(1)` en zonder de controle op dubbele waarden. Twee net toegevoegde
 * opties hebben allebei nog een lege waarde, en dat is geen fout maar een halve
 * invoer; `verhard` meldt beide.
 */
const OptionsDraft = z.array(OptionDraft)

/** Bij een cijferbereik staan de grenzen er nog niet zodra de schaal net is gekozen. */
const ScaleDraft = z.discriminatedUnion('soort', [
  z.strictObject({
    soort: z.literal('tweekeuze'),
    opties: z.tuple([OptionDraft, OptionDraft]).optional(),
  }),
  z.strictObject({
    soort: z.literal('cijferbereik'),
    minimum: z.number().int().optional(),
    maximum: z.number().int().optional(),
    waarden: z.array(z.strictObject({ waarde: z.number().int(), label: TextDraft })).optional(),
  }),
])

/** Contract en pad mogen half ingevuld zijn zolang de inrichter de catalogus doorzoekt. */
const SourceDraft = z.strictObject({ contract: z.string(), pad: z.string() })

// Zelfde vorm als in `veld.ts`, en dat wordt met een test afgedwongen: de conceptboom draagt
// per controltype precies dezelfde eigenschappen als de strikte boom. Het herkomstmerk van
// D-52 hoort er dus ook hier bij, en het is aan beide kanten optioneel.
const core = { id: FieldId, uitStandaardveld: FieldOrigin.optional() }
const goesBack = z.boolean().optional()
const labeled = {
  ...core,
  label: TextDraft,
  hulptekst: TextDraft.optional(),
  gaatTerug: goesBack,
  /** Optioneel, net als in de strikte boom; `Definitie` dwingt af waar hij hoort te staan. */
  compartiment: CompartimentId.optional(),
  /** Optioneel, net als in de strikte boom (R-170, D-54); weggelaten betekent ja. */
  externZichtbaar: z.boolean().optional(),
  /** Optioneel, net als in de strikte boom (R-54, R-56); de naam mag in een concept nog leeg zijn. */
  dossierveld: z.strictObject({ naam: z.string(), aanpasbaar: z.boolean() }).optional(),
}

/** Generiek, net als in `veld.ts`: alleen zo houdt elke tak één letterlijke `control`. */
function withoutSettings<C extends Control>(control: C) {
  return z.strictObject({ ...labeled, control: z.literal(control) })
}

const SIMPLE = [
  withoutSettings('text'),
  withoutSettings('textarea'),
  withoutSettings('number'),
  withoutSettings('currency'),
  withoutSettings('url'),
  withoutSettings('email'),
  withoutSettings('phone'),
  withoutSettings('richtext'),
  withoutSettings('date'),
  withoutSettings('datetime'),
  withoutSettings('lookup'),
  withoutSettings('tag'),
  withoutSettings('rating'),
  withoutSettings('slider'),
  withoutSettings('file'),
  withoutSettings('image'),
  withoutSettings('status'),
  withoutSettings('notification'),
  withoutSettings('progress'),
  withoutSettings('timer'),
] as const

const SUBGRID = [
  z.strictObject({ ...labeled, control: z.literal('subgrid'), bron: SourceDraft.optional() }),
] as const

const TOGGLE = [
  z.strictObject({ ...labeled, control: z.literal('toggle'), akkoord: z.boolean().optional() }),
] as const

const CHOICE = [
  z.strictObject({ ...labeled, control: z.literal('dropdown'), opties: OptionsDraft }),
  z.strictObject({ ...labeled, control: z.literal('multiselect'), opties: OptionsDraft }),
  z.strictObject({ ...labeled, control: z.literal('radio'), opties: OptionsDraft }),
  z.strictObject({ ...labeled, control: z.literal('checkbox'), opties: OptionsDraft.optional() }),
] as const

export const DraftRowField = z.discriminatedUnion('control', [
  ...SIMPLE,
  ...TOGGLE,
  ...SUBGRID,
  ...CHOICE,
])
export type DraftRowField = z.infer<typeof DraftRowField>

/** Een net neergezet criterium heeft nog geen id en nog geen schaal (D-57, R-148). */
const criterion = z.strictObject({
  ...labeled,
  control: z.literal('criterium'),
  criteriumId: CriterionId.optional(),
  /** De vraag waarover dit criterium gaat (D-55); zie `veld.ts` voor waarom hij optioneel is. */
  hoortBijVeld: FieldId.optional(),
  schaal: ScaleDraft.optional(),
  onderbouwing: z.strictObject({ label: TextDraft }).optional(),
  controle: z.string().optional(),
})

/** Een net neergezette herhaalgroep heeft nog geen rijvelden (D-06). */
const repeatGroup = z.strictObject({
  ...labeled,
  control: z.literal('herhaalgroep'),
  rijVelden: z.array(DraftRowField),
})

/** Het akkoord per persoon (eisenboek 12.56): alleen een label, zoals in `veld.ts`. */
const consentPerPerson = z.strictObject({ ...labeled, control: z.literal('akkoordPerPersoon') })

/** Een net overgenomen dossierveld heeft nog geen pad zolang de catalogus openstaat (D-07). */
const verwijzing = z.strictObject({
  ...labeled,
  control: z.literal('verwijzing'),
  bron: SourceDraft.optional(),
})

/** `component` blijft verplicht: het palet levert een specifiek component en geen keuze achteraf. */
const component = z.strictObject({
  ...core,
  control: z.literal('component'),
  component: z.enum(COMPONENTS),
  label: TextDraft.optional(),
})

export const DraftField = z.discriminatedUnion('control', [
  ...SIMPLE,
  ...TOGGLE,
  ...SUBGRID,
  ...CHOICE,
  criterion,
  repeatGroup,
  verwijzing,
  component,
  consentPerPerson,
])
export type DraftField = z.infer<typeof DraftField>

/** Een sectie zonder velden is een sectie waar de inrichter net aan is begonnen (D-01). */
export const DraftSection = z.strictObject({
  id: SectionId,
  type: z.enum(['gewoon', 'bron', 'toets']),
  titel: TextDraft,
  uitleg: TextDraft.optional(),
  kolommen: z.union([z.literal(1), z.literal(2)]),
  hoortBij: SectionId.optional(),
  velden: z.array(DraftField),
  regels: z.array(Rule),
})
export type DraftSection = z.infer<typeof DraftSection>

/**
 * Geen `superRefine`, en dat is opzet. Alle controles die `Definitie` over secties
 * heen doet, van unieke id's tot een regeldoel dat bestaat, gaan uit van een complete
 * boom; op een half formulier melden ze gaten die de inrichter nog aan het dichten is.
 * `verhard` draait ze allemaal, in één keer, op het moment dat het uitmaakt.
 */
export const DraftDefinitie = z.strictObject({
  id: DefinitieId,
  /**
   * Verplicht, ook in een concept. Een formulier wordt gemaakt vóór een bouwblok en
   * niet later aan een bouwblok gehangen: de bouwblokken van een ronde staan vast
   * voordat de formulieren worden gemaakt (D-41). Zonder blok weet de configurator
   * niet welke compartimenten hij mag aanbieden (D-69) en welke paden verwijsbaar zijn (D-38).
   */
  blok: Block,
  /** Dezelfde instelling als op `Definitie`, en even optioneel (besluit van 30 oktober 2026). */
  advies: AdviesSetting.optional(),
  /** Dezelfde instelling als op `Definitie`, en even optioneel (R-184h). */
  belangenverklaring: BelangenverklaringSetting.optional(),
  titel: TextDraft,
  /** Dezelfde instelling als op `Definitie`, en even optioneel (D-59). */
  werkverzameling: WerkverzamelingSetting.optional(),
  /** Dezelfde instelling als op `Definitie`, en even optioneel (besluit van 30 oktober 2026). */
  getoetst: z.boolean().optional(),
  /** Dezelfde instelling als op `Definitie` (R-04); afwezig is aan. */
  nummering: z.boolean().optional(),
  /** Uit welk sjabloon en welke versie dit formulier is gemaakt; inert, zoals op `Definitie` (D-34). */
  uitSjabloon: TemplateOrigin.optional(),
  secties: z.array(DraftSection),
})
export type DraftDefinitie = z.infer<typeof DraftDefinitie>

/**
 * Alle velden van een conceptsectie, inclusief de rijvelden van een herhaalgroep.
 *
 * De tegenhanger van `velden` in `definitie.ts`, en om dezelfde reden een tweede functie
 * als er twee bomen zijn: die loopt over een strikte `Sectie` en levert `Veld`, deze over
 * een `DraftSection` en levert `ConceptVeld`. Een generieke variant zou het verschil
 * tussen de twee juist wegnemen, en dat verschil is precies waarvoor de conceptboom
 * bestaat.
 *
 * `concept.test.ts` legt vast dat beide wandelaars over dezelfde vorm dezelfde paden
 * opleveren, zodat ze niet uiteen kunnen lopen.
 */
export function draftFields(section: DraftSection): { veld: DraftField; pad: (string | number)[] }[] {
  const out: { veld: DraftField; pad: (string | number)[] }[] = []
  section.velden.forEach((field, v) => {
    out.push({ veld: field, pad: ['velden', v] })
    if (field.control === 'herhaalgroep') {
      field.rijVelden.forEach((rowField, r) => {
        out.push({ veld: rowField, pad: ['velden', v, 'rijVelden', r] })
      })
    }
  })
  return out
}
